import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Play, Square, Coffee, ArrowLeft, Clock, ScanLine } from "lucide-react";

const TYPE_LABEL = {
  clock_in: "Clock in", clock_out: "Clock out",
  break_start: "Break start", break_end: "Break end",
};

const daysAgo = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0,10);
};

export default function AttendancePage() {
  const { branchId, hasPerm } = useAuth();
  const [tab, setTab] = useState("live");
  const [onClock, setOnClock] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [events, setEvents] = useState([]);
  const [timesheet, setTimesheet] = useState(null);
  const [range, setRange] = useState({ from: daysAgo(7), to: new Date().toISOString().slice(0,10) });
  const [scanCode, setScanCode] = useState("");
  const [scanResult, setScanResult] = useState(null);

  const load = async () => {
    if (!branchId) return;
    try {
      const [oc, emp, ev] = await Promise.all([
        api.get("/attendance/on-clock", { params: { branch_id: branchId } }),
        employees.length ? Promise.resolve({ data: employees }) : api.get("/employees", { params: { branch_id: branchId, active: true } }),
        api.get("/attendance/events", { params: { branch_id: branchId, limit: 100 } }),
      ]);
      setOnClock(oc.data);
      if (!employees.length) setEmployees(emp.data);
      setEvents(ev.data);
    } catch (_) {}
  };
  useEffect(() => { load(); const iv = setInterval(load, 8000); return () => clearInterval(iv); }, [branchId]);

  const loadTimesheet = async () => {
    if (!branchId) return;
    try {
      const { data } = await api.get("/attendance/timesheet", {
        params: { branch_id: branchId, date_from: range.from, date_to: range.to },
      });
      setTimesheet(data);
    } catch (_) {}
  };
  useEffect(() => { if (tab === "timesheet") loadTimesheet(); }, [tab, range]);

  const doEvent = async (emp_id, type) => {
    try {
      await api.post("/attendance/events", {
        employee_id: emp_id, branch_id: branchId, type, source: "manual",
      });
      toast.success(TYPE_LABEL[type]);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const scan = async () => {
    if (!scanCode.trim()) return;
    try {
      const { data } = await api.post("/attendance/scan-staff", {
        branch_id: branchId, employee_code: scanCode.trim(),
      });
      setScanResult(data);
      toast.success(`${data.event.employee_name} — ${TYPE_LABEL[data.event.type]}`);
      setScanCode(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setScanResult(null); }
  };

  const isOnClock = (emp_id) => onClock.find((o) => o.employee_id === emp_id);

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="attendance-page">
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">HR · Attendance</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Clock &amp; timesheet</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Live who's on the floor plus a period timesheet. Scan badge codes for one-tap clock toggle, or push a manual event for corrections.
          </p>
        </div>
        <div className="text-right">
          <div className="section-eyebrow">On the clock</div>
          <div className="mt-3 display-heading text-5xl ink mono" data-testid="att-on-clock-count">{onClock.length}</div>
        </div>
      </div>

      <div className="mt-10 border-b hairline-soft animate-fade-up animate-fade-up-1">
        <div className="flex gap-6 -mb-px">
          {[
            { k: "live",      label: "Live board" },
            { k: "log",       label: "Event log" },
            { k: "timesheet", label: "Timesheet" },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
                    data-testid={`att-tab-${t.k}`}
                    className={`pb-4 text-[11px] uppercase tracking-[0.24em] font-bold transition-colors border-b-2 ${
                      tab === t.k ? "border-foreground ink" : "border-transparent text-foreground/40 hover:text-foreground/70"
                    }`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {!branchId && (
        <div className="mt-14 border hairline-soft rounded-md p-8 text-center ink-subtle text-sm">
          Select a branch first.
        </div>
      )}

      {branchId && tab === "live" && (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-10 animate-fade-up animate-fade-up-2">
          {/* Scan panel */}
          {hasPerm("attendance.manage") && (
            <div className="lg:col-span-1 border hairline surface-card rounded-lg p-6 h-fit">
              <div className="section-eyebrow">Badge scan</div>
              <div className="mt-4">
                <input autoFocus value={scanCode} onChange={(e)=>setScanCode(e.target.value.toUpperCase())}
                       onKeyDown={(e) => e.key === "Enter" && scan()}
                       placeholder="EMP001" data-testid="att-scan-code"
                       className="h-14 w-full rounded-md border hairline surface-2 px-4 mono ink text-2xl font-bold tracking-widest text-center"/>
                <button onClick={scan} disabled={!scanCode.trim()} data-testid="att-scan-btn"
                        className="mt-3 btn-pill btn-pill-solid h-11 w-full disabled:opacity-40">
                  <ScanLine size={14}/> Toggle clock
                </button>
              </div>
              {scanResult && (
                <div className={`mt-4 border rounded-md p-3 text-xs ${scanResult.now_on_clock ? "border-emerald-500/40 bg-emerald-500/5" : "border-foreground/20"}`}>
                  <div className="tiny-label">Last scan</div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.24em] font-bold ink">{scanResult.event.employee_name}</div>
                  <div className="text-xs ink-subtle mt-1">{TYPE_LABEL[scanResult.event.type]} · {new Date(scanResult.event.at).toLocaleTimeString("en-PH")}</div>
                </div>
              )}
            </div>
          )}

          {/* Employee board */}
          <div className="lg:col-span-2">
            <div className="section-eyebrow mb-4">Roster</div>
            <div className="border-y hairline-soft divide-y hairline-soft" data-testid="att-roster">
              {employees.map((e) => {
                const clocked = isOnClock(e.id);
                return (
                  <div key={e.id} className="py-3 flex items-center gap-3" data-testid={`att-row-${e.id}`}>
                    <div className={`h-9 w-9 rounded-full flex items-center justify-center text-[10px] uppercase tracking-widest font-bold ${
                      clocked?.state === "on_break" ? "bg-amber-500/20 text-amber-500"
                        : clocked ? "bg-emerald-500/20 text-emerald-500"
                        : "bg-foreground/10 text-foreground/60"
                    }`}>
                      {e.name.split(" ").map((n)=>n[0]).slice(0,2).join("")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{e.name}</div>
                      <div className="text-xs ink-subtle mt-0.5">
                        {e.position} · {e.code}
                        {clocked && <span className="ml-2 text-emerald-500 mono">since {new Date(clocked.since).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}</span>}
                      </div>
                    </div>
                    {hasPerm("attendance.manage") && (
                      <div className="flex items-center gap-1">
                        {!clocked && (
                          <button onClick={() => doEvent(e.id, "clock_in")} data-testid={`att-clock-in-${e.id}`}
                                  className="h-8 px-3 rounded-full border border-emerald-500/40 text-emerald-500 text-[10px] uppercase tracking-[0.22em] font-bold hover:bg-emerald-500/5">
                            <Play size={11} className="inline -mt-0.5"/> In
                          </button>
                        )}
                        {clocked?.state === "on_clock" && (
                          <>
                            <button onClick={() => doEvent(e.id, "break_start")} data-testid={`att-break-${e.id}`}
                                    className="h-8 px-3 rounded-full border hairline text-[10px] uppercase tracking-[0.22em] font-bold hover:bg-foreground/5">
                              <Coffee size={11} className="inline -mt-0.5"/> Break
                            </button>
                            <button onClick={() => doEvent(e.id, "clock_out")} data-testid={`att-clock-out-${e.id}`}
                                    className="h-8 px-3 rounded-full border border-red-500/40 text-red-500 text-[10px] uppercase tracking-[0.22em] font-bold hover:bg-red-500/5">
                              <Square size={11} className="inline -mt-0.5"/> Out
                            </button>
                          </>
                        )}
                        {clocked?.state === "on_break" && (
                          <button onClick={() => doEvent(e.id, "break_end")} data-testid={`att-break-end-${e.id}`}
                                  className="h-8 px-3 rounded-full border border-amber-500/40 text-amber-500 text-[10px] uppercase tracking-[0.22em] font-bold hover:bg-amber-500/5">
                            <ArrowLeft size={11} className="inline -mt-0.5"/> Resume
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {employees.length === 0 && <div className="py-6 ink-subtle text-center text-sm">No employees.</div>}
            </div>
          </div>
        </div>
      )}

      {branchId && tab === "log" && (
        <div className="mt-10 border-y hairline-soft divide-y hairline-soft animate-fade-up animate-fade-up-2" data-testid="att-log">
          <div className="grid grid-cols-12 py-3 tiny-label ink-subtle">
            <div className="col-span-2">Time</div>
            <div className="col-span-3">Employee</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2">Source</div>
            <div className="col-span-3">Notes</div>
          </div>
          {events.length === 0 && <div className="py-6 ink-subtle text-center text-sm">No events yet.</div>}
          {events.map((e) => (
            <div key={e.id} className="grid grid-cols-12 py-3 items-center">
              <div className="col-span-2 mono text-[11px] ink-subtle">
                {new Date(e.at).toLocaleString("en-PH", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
              </div>
              <div className="col-span-3 text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{e.employee_name}</div>
              <div className="col-span-2 tiny-label">{TYPE_LABEL[e.type] || e.type}</div>
              <div className="col-span-2 tiny-label ink-subtle">{e.source}</div>
              <div className="col-span-3 text-xs ink-subtle italic truncate">{e.notes || "—"}</div>
            </div>
          ))}
        </div>
      )}

      {branchId && tab === "timesheet" && (
        <div className="mt-10 animate-fade-up animate-fade-up-2">
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2">
              <span className="tiny-label ink-subtle">From</span>
              <input type="date" value={range.from} onChange={(e)=>setRange({...range, from: e.target.value})}
                     className="h-9 rounded-md border hairline surface-card px-3 mono ink text-sm" data-testid="att-ts-from"/>
            </label>
            <label className="flex items-center gap-2">
              <span className="tiny-label ink-subtle">To</span>
              <input type="date" value={range.to} onChange={(e)=>setRange({...range, to: e.target.value})}
                     className="h-9 rounded-md border hairline surface-card px-3 mono ink text-sm" data-testid="att-ts-to"/>
            </label>
            {timesheet && (
              <div className="ml-auto flex items-center gap-6">
                <Stat label="Total hours" value={`${timesheet.totals.hours}h`}/>
                <Stat label="Est. wages" value={peso(timesheet.totals.estimated_wage)} accent/>
              </div>
            )}
          </div>

          {timesheet && (
            <div className="mt-6 border-y hairline-soft divide-y hairline-soft" data-testid="att-ts-table">
              <div className="grid grid-cols-12 py-3 tiny-label ink-subtle">
                <div className="col-span-3">Employee</div>
                <div className="col-span-2">Pay</div>
                <div className="col-span-2 text-right">Shifts</div>
                <div className="col-span-2 text-right">Hours</div>
                <div className="col-span-3 text-right">Estimated wage</div>
              </div>
              {timesheet.rows.length === 0 && <div className="py-6 ink-subtle text-center text-sm">No timesheet data in this range.</div>}
              {timesheet.rows.map((r) => (
                <div key={r.employee_id} className="grid grid-cols-12 py-3 items-center" data-testid={`att-ts-row-${r.employee_id}`}>
                  <div className="col-span-3 pr-2">
                    <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{r.name}</div>
                    <div className="text-xs ink-subtle mono">{r.code} · {r.position}</div>
                  </div>
                  <div className="col-span-2 tiny-label">
                    {r.pay_type === "hourly" ? `${peso(r.pay_rate)}/hr`
                      : r.pay_type === "per_shift" ? `${peso(r.pay_rate)}/shift`
                      : `${peso(r.pay_rate)}/mo`}
                  </div>
                  <div className="col-span-2 text-right mono text-sm ink">{r.shifts}</div>
                  <div className="col-span-2 text-right mono text-sm ink">{r.hours}h</div>
                  <div className="col-span-3 text-right mono text-sm ink font-semibold">{peso(r.estimated_wage)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="tiny-label ink-subtle">{label}</div>
      <div className={`mono ink text-lg font-semibold ${accent ? "border-l-2 border-foreground pl-2" : ""}`}>{value}</div>
    </div>
  );
}
